// Braking Distance Calculator: field metadata, client-side per-field validation, road
// surface presets, and unit conversion constants. The actual physics (weight transfer,
// friction-vs-brake-system-torque solve) and authoritative validation live server-side
// -- see src/lib/toolsApi.js (brakingDistanceToolApi) and
// backend/tools/braking_distance_tool.py. Regenerative braking is explicitly out of
// scope for this tool.

export const KMPH_TO_MPH = 1 / 1.609344;
export const MPH_TO_KMPH = 1.609344;
export const KG_TO_LB = 2.20462262185;
export const LB_TO_KG = 1 / KG_TO_LB;

// ABS-OFF locked-wheel friction utilization factor -- confirmed with the user
// (2026-08-18) as 70% of the ABS-modulated peak friction coefficient, matching
// backend/core/lookup/braking_distance_specs.py::ABS_OFF_FRICTION_FACTOR.
export const ABS_OFF_FRICTION_FACTOR = 0.70;

export const ROAD_SURFACE_PRESETS = [
  { value: 'dry_asphalt', label: 'Dry Asphalt / Concrete', mu: 0.80 },
  { value: 'wet_asphalt', label: 'Wet Asphalt / Concrete', mu: 0.50 },
  { value: 'gravel', label: 'Gravel', mu: 0.60 },
  { value: 'snow', label: 'Packed Snow', mu: 0.20 },
  { value: 'ice', label: 'Ice', mu: 0.10 },
  { value: 'custom', label: 'Custom…', mu: null },
];

export const DEFAULT_INPUTS = {
  mode: 'basic',

  vehicleMassKg: 1500,
  initialSpeedKmph: 100,
  reactionTimeS: 1.5,
  frictionCoefficient: 0.80,
  gradePercent: 0,
  absEnabled: true,

  wheelbaseM: 2.7,
  cgHeightM: 0.55,
  frontWeightDistPercent: 55,
  frontBrakeForceDistPercent: 70,
  wheelRadiusM: 0.32,
  brakeTorqueNm: 4000,
  brakeResponseTimeS: 0.15,
  frontMu: 0.80,
  rearMu: 0.80,
};

// min / max mirror NUMERIC_FIELD_SPECS in backend/core/lookup/braking_distance_specs.py.
export const FIELD_SPECS = [
  { key: 'vehicleMassKg', label: 'Vehicle Mass', unit: 'kg', min: 500, max: 4000, strictlyPositive: true },
  { key: 'initialSpeedKmph', label: 'Initial Speed', unit: 'km/h', min: 0, max: 250, strictlyPositive: false },
  { key: 'reactionTimeS', label: 'Reaction Time', unit: 's', min: 0.1, max: 3.0, strictlyPositive: true },
  { key: 'frictionCoefficient', label: 'Friction Coefficient', unit: 'μ', min: 0.05, max: 1.2, strictlyPositive: true },
  { key: 'gradePercent', label: 'Road Gradient', unit: '%', min: -20, max: 20, strictlyPositive: false },

  { key: 'wheelbaseM', label: 'Wheelbase', unit: 'm', min: 1.5, max: 4.5, strictlyPositive: true },
  { key: 'cgHeightM', label: 'CG Height', unit: 'm', min: 0.3, max: 1.5, strictlyPositive: true },
  { key: 'frontWeightDistPercent', label: 'Front Weight Distribution', unit: '%', min: 30, max: 80, strictlyPositive: true },
  { key: 'frontBrakeForceDistPercent', label: 'Front Brake Force Distribution', unit: '%', min: 30, max: 90, strictlyPositive: true },
  { key: 'wheelRadiusM', label: 'Wheel / Tyre Radius', unit: 'm', min: 0.2, max: 0.6, strictlyPositive: true },
  { key: 'brakeTorqueNm', label: 'Brake Torque (Total System)', unit: 'Nm', min: 200, max: 30000, strictlyPositive: true },
  { key: 'brakeResponseTimeS', label: 'Brake System Response Time', unit: 's', min: 0, max: 1.0, strictlyPositive: false },
  { key: 'frontMu', label: 'Front Tyre Friction Coefficient', unit: 'μ', min: 0.05, max: 1.2, strictlyPositive: true },
  { key: 'rearMu', label: 'Rear Tyre Friction Coefficient', unit: 'μ', min: 0.05, max: 1.2, strictlyPositive: true },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
